import { FORMULA_ARITY, FORMULA_OPS, MAX_FORMULA_DEPTH, MAX_FORMULA_NODES } from "./formula.js";
import { INDICATOR_KINDS, INDICATOR_OUTPUTS, INDICATOR_TIMEFRAMES } from "./indicator.js";
import { MAX_OPERAND_OFFSET, OPERAND_KINDS, PRICE_FIELDS } from "./spec.js";
function isObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Why an operand's `offset` is not acceptable, or null. Absent is fine. */
export function offsetProblem(operand) {
    if (!("offset" in operand) || operand.offset === undefined)
        return null;
    const offset = operand.offset;
    if (operand.kind === "const")
        return "a constant has no earlier bars; remove the offset";
    if (typeof offset !== "number" || !Number.isInteger(offset) || offset < 0) {
        return "must be a whole number of bars, 0 or more";
    }
    if (offset > MAX_OPERAND_OFFSET)
        return `at most ${MAX_OPERAND_OFFSET} bars back`;
    return null;
}
/**
 * Problems with the indicators' `timeframe` and `formula` fields, and with
 * formulas reading each other in a circle. Rule operands' offsets are checked
 * where the rules are walked (`offsetProblem`).
 */
export function indicatorExtensionProblems(indicators) {
    const out = [];
    if (!Array.isArray(indicators))
        return out;
    const byId = new Map();
    for (const def of indicators) {
        if (isObject(def) && typeof def.id === "string")
            byId.set(def.id, def);
    }
    indicators.forEach((def, i) => {
        if (!isObject(def))
            return;
        const at = `indicators[${i}]`;
        if (def.timeframe !== undefined) {
            if (!INDICATOR_TIMEFRAMES.includes(def.timeframe)) {
                out.push({
                    id: "indicator-timeframe",
                    field: `${at}.timeframe`,
                    message: `must be one of ${INDICATOR_TIMEFRAMES.join(", ")}`,
                });
            }
            if (def.source !== undefined) {
                out.push({
                    id: "indicator-timeframe",
                    field: `${at}.timeframe`,
                    message: "an indicator reading another market takes that market's timeframe; drop one of the two",
                });
            }
        }
        if (def.kind === "formula") {
            if (def.source !== undefined || def.timeframe !== undefined) {
                out.push({
                    id: "formula-shape",
                    field: at,
                    message: "a formula is computed on the run's own bars; put the market or timeframe on the indicators it reads",
                });
            }
            if (def.formula === undefined) {
                out.push({ id: "formula-shape", field: `${at}.formula`, message: "is required for a formula" });
            }
            else {
                checkFormula(def.formula, `${at}.formula`, typeof def.id === "string" ? def.id : "", byId, out);
            }
        }
        else if (def.formula !== undefined) {
            out.push({
                id: "formula-shape",
                field: `${at}.formula`,
                message: 'only an indicator of kind "formula" carries a formula',
            });
        }
    });
    out.push(...formulaCycles(indicators));
    return out;
}
function checkFormula(root, at, selfId, byId, out) {
    let nodes = 0;
    const walk = (node, path, depth) => {
        nodes += 1;
        if (nodes === MAX_FORMULA_NODES + 1) {
            out.push({ id: "formula-size", field: at, message: `more than ${MAX_FORMULA_NODES} parts` });
        }
        if (nodes > MAX_FORMULA_NODES)
            return;
        if (depth > MAX_FORMULA_DEPTH) {
            out.push({ id: "formula-size", field: path, message: `nested more than ${MAX_FORMULA_DEPTH} deep` });
            return;
        }
        if (!isObject(node)) {
            out.push({ id: "formula-shape", field: path, message: "must be an object" });
            return;
        }
        if ("op" in node || "args" in node) {
            const op = node.op;
            if (typeof op !== "string" || !FORMULA_OPS.includes(op)) {
                out.push({
                    id: "formula-op",
                    field: `${path}.op`,
                    message: `must be one of ${FORMULA_OPS.join(", ")}`,
                });
                return;
            }
            const arity = FORMULA_ARITY[op];
            if (!Array.isArray(node.args) || node.args.length !== arity) {
                out.push({
                    id: "formula-op",
                    field: `${path}.args`,
                    message: `${op} takes ${arity} argument${arity === 1 ? "" : "s"}`,
                });
                return;
            }
            node.args.forEach((arg, k) => walk(arg, `${path}.args[${k}]`, depth + 1));
            return;
        }
        // A leaf: an operand, as a rule would write it.
        const kind = node.kind;
        if (typeof kind !== "string" || !OPERAND_KINDS.includes(kind)) {
            out.push({
                id: "formula-operand",
                field: `${path}.kind`,
                message: `must be one of ${OPERAND_KINDS.join(", ")}`,
            });
            return;
        }
        const offset = offsetProblem(node);
        if (offset)
            out.push({ id: "operand-offset", field: `${path}.offset`, message: offset });
        if (kind === "const") {
            if (typeof node.value !== "number" || !Number.isFinite(node.value)) {
                out.push({ id: "formula-operand", field: `${path}.value`, message: "must be a finite number" });
            }
        }
        else if (kind === "price") {
            if (typeof node.field !== "string" || !PRICE_FIELDS.includes(node.field)) {
                out.push({
                    id: "formula-operand",
                    field: `${path}.field`,
                    message: `must be one of ${PRICE_FIELDS.join(", ")}`,
                });
            }
        }
        else {
            const ref = node.ref;
            if (typeof ref !== "string" || ref === "") {
                out.push({ id: "formula-operand", field: `${path}.ref`, message: "must name an indicator" });
            }
            else if (ref === selfId) {
                out.push({ id: "formula-cycle", field: `${path}.ref`, message: "a formula cannot read itself" });
            }
            else if (!byId.has(ref)) {
                out.push({
                    id: "formula-operand",
                    field: `${path}.ref`,
                    message: `"${ref}" names no indicator on this strategy`,
                });
            }
            else if (node.output !== undefined) {
                const target = byId.get(ref)?.kind;
                const outputs = INDICATOR_KINDS.includes(target)
                    ? INDICATOR_OUTPUTS[target]
                    : undefined;
                if (typeof node.output !== "string" || (outputs && !outputs.includes(node.output))) {
                    out.push({
                        id: "formula-operand",
                        field: `${path}.output`,
                        message: `${String(target)} has no output "${String(node.output)}"${outputs ? `. Try ${outputs.join(", ")}` : ""}`,
                    });
                }
            }
        }
    };
    walk(root, at, 1);
}
/**
 * Formulas reading each other in a circle — a reads b, b reads a — have no
 * first one to compute. Reported once per circle, on its first member.
 */
function formulaCycles(indicators) {
    const reads = new Map();
    const index = new Map();
    indicators.forEach((def, i) => {
        if (!isObject(def) || def.kind !== "formula" || typeof def.id !== "string")
            return;
        index.set(def.id, i);
        const refs = [];
        const collect = (node, depth) => {
            if (!isObject(node) || depth > MAX_FORMULA_DEPTH + 1)
                return;
            if (Array.isArray(node.args))
                node.args.forEach((a) => collect(a, depth + 1));
            else if (node.kind === "indicator" && typeof node.ref === "string" && node.ref !== def.id)
                refs.push(node.ref);
        };
        collect(def.formula, 0);
        reads.set(def.id, refs);
    });
    const out = [];
    const state = new Map();
    const reported = new Set();
    const visit = (id, trail) => {
        if (state.get(id) === "done")
            return;
        if (state.get(id) === "visiting") {
            const circle = trail.slice(trail.indexOf(id));
            const first = [...circle].sort((a, b) => (index.get(a) ?? 0) - (index.get(b) ?? 0))[0];
            if (!reported.has(first)) {
                circle.forEach((c) => reported.add(c));
                out.push({
                    id: "formula-cycle",
                    field: `indicators[${index.get(first)}].formula`,
                    message: `formulas read each other in a circle: ${[...circle, id].join(" → ")}`,
                });
            }
            return;
        }
        state.set(id, "visiting");
        for (const ref of reads.get(id) ?? [])
            if (reads.has(ref))
                visit(ref, [...trail, id]);
        state.set(id, "done");
    };
    for (const id of reads.keys())
        visit(id, []);
    return out;
}
/**
 * Formula indicators in an order where each comes after every formula it
 * reads. Assumes no cycles (`indicatorExtensionProblems` refuses them); one
 * that slipped through is left at the end, where it reads nulls.
 */
export function formulaOrder(defs) {
    const formulas = defs.filter((d) => d.kind === "formula");
    const ids = new Set(formulas.map((d) => d.id));
    const done = new Set();
    const ordered = [];
    const refsOf = (def) => {
        const refs = [];
        const collect = (node, depth) => {
            if (!isObject(node) || depth > MAX_FORMULA_DEPTH + 1)
                return;
            if (Array.isArray(node.args))
                node.args.forEach((a) => collect(a, depth + 1));
            else if (node.kind === "indicator" && typeof node.ref === "string" && ids.has(node.ref))
                refs.push(node.ref);
        };
        collect(def.formula, 0);
        return refs;
    };
    let progressed = true;
    while (progressed && ordered.length < formulas.length) {
        progressed = false;
        for (const def of formulas) {
            if (done.has(def.id))
                continue;
            if (refsOf(def).every((r) => r === def.id || done.has(r))) {
                ordered.push(def);
                done.add(def.id);
                progressed = true;
            }
        }
    }
    for (const def of formulas)
        if (!done.has(def.id))
            ordered.push(def);
    return ordered;
}
//# sourceMappingURL=extensions.js.map